import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  Path,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";

import { FILL_OPTION } from "~/services";
import { Button, useFieldErrorVisibility } from "~/shared";
import { sexAtBirthOptions } from "../../data/constants";
import {
  AddressSection,
  BookingNotesSection,
  CancellationPolicySection,
  ConsentsSection,
  ContactInfoSection,
  GeneralInfoSection,
  InsuranceSection,
  OptionalAddressSection,
  OptionalInsuranceSection,
  ReferralSourceSection,
  StateDisclaimerSection,
} from "./visit-form-sections";

type FieldErrorVisibility = ReturnType<typeof useFieldErrorVisibility>;

const untouchedFieldErrorMap: z.core.$ZodErrorMap = (issue) =>
  issue.code === "invalid_type" && issue.input === undefined
    ? "This field is required"
    : undefined;

type VisitFormContextValue<T extends z.ZodType<any, any, any>> = {
  form: UseFormReturn<z.input<T> & FieldValues, any, z.output<T> & FieldValues>;
  fieldErrorVisibility: FieldErrorVisibility;
  insuranceCardFront: File | null;
  insuranceCardBack: File | null;
  setInsuranceCardFront: (file: File | null) => void;
  setInsuranceCardBack: (file: File | null) => void;
  isLoading: boolean;
  forceInsuranceNow: boolean;
};

const VisitFormContext = createContext<VisitFormContextValue<any> | null>(null);

export const useVisitFormContext = <T extends z.ZodType>() => {
  const context = useContext(
    VisitFormContext,
  ) as VisitFormContextValue<T> | null;
  if (!context) {
    throw new Error("useVisitFormContext must be used within a VisitForm");
  }
  return context;
};

type VisitFormProps<T extends z.ZodType> = {
  schema: T;
  onSubmit: (data: z.output<T>) => void;
  isLoading?: boolean;
  children: React.ReactNode;
  fillInsuranceLater?: boolean;
  fillInsuranceNow?: boolean;
  fillAddressLater?: boolean;
  forceInsuranceNow?: boolean;
  initialValues?: Partial<z.input<T>>;
};

export function VisitForm<T extends z.ZodType<any, any, any>>({
  schema,
  onSubmit,
  isLoading = false,
  children,
  fillInsuranceLater = false,
  fillInsuranceNow = false,
  fillAddressLater = false,
  forceInsuranceNow = false,
  initialValues,
}: VisitFormProps<T>) {
  const defaultValues = {
    sexAtBirth: sexAtBirthOptions[0]?.value,
    fillInsuranceInfo: forceInsuranceNow
      ? FILL_OPTION.NOW
      : fillInsuranceLater
        ? FILL_OPTION.LATER
        : fillInsuranceNow
          ? FILL_OPTION.NOW
          : undefined,
    fillAddressInfo: fillAddressLater ? FILL_OPTION.LATER : undefined,
    ...initialValues,
  } as DefaultValues<z.input<T> & FieldValues>;

  const form = useForm<
    z.input<T> & FieldValues,
    any,
    z.output<T> & FieldValues
  >({
    resolver: zodResolver(schema, { error: untouchedFieldErrorMap }),
    mode: "onChange",
    defaultValues,
  });

  const [insuranceCardFront, setInsuranceCardFront] = useState<File | null>(
    null,
  );
  const [insuranceCardBack, setInsuranceCardBack] = useState<File | null>(null);

  const fieldErrorVisibility = useFieldErrorVisibility();
  const formRef = useRef<HTMLFormElement>(null);
  const shouldFocusFirstInvalidField = useRef(false);

  const handleInvalidSubmit = (
    errors: FieldErrors<z.input<T> & FieldValues>,
  ) => {
    Object.keys(errors).forEach((field) =>
      fieldErrorVisibility.showError(field),
    );
    shouldFocusFirstInvalidField.current = true;
  };

  useEffect(() => {
    if (!shouldFocusFirstInvalidField.current) return;

    const firstInvalidField = formRef.current?.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );
    if (!firstInvalidField) return;

    shouldFocusFirstInvalidField.current = false;
    firstInvalidField.focus();
  }, [form.formState.errors, fieldErrorVisibility.visibleErrors]);

  const handleSetInsuranceCardFront = (file: File | null) => {
    setInsuranceCardFront(file);
    form.setValue(
      "insuranceCardFront" as Path<z.input<T> & FieldValues>,
      file as (z.input<T> & FieldValues)["insuranceCardFront"],
      { shouldValidate: true },
    );
  };

  const handleSetInsuranceCardBack = (file: File | null) => {
    setInsuranceCardBack(file);
    form.setValue(
      "insuranceCardBack" as Path<z.input<T> & FieldValues>,
      file as (z.input<T> & FieldValues)["insuranceCardBack"],
      { shouldValidate: true },
    );
  };

  const handleFormSubmit = (data: z.infer<T>) => {
    const isFillingInsuranceLater =
      data.fillInsuranceInfo === FILL_OPTION.LATER;
    const isFillingAddressNow = data.fillAddressInfo === FILL_OPTION.NOW;

    let formData = {
      ...data,
      insuranceCardFront,
      insuranceCardBack,
    };

    if (isFillingInsuranceLater) {
      formData = {
        ...formData,
        relationshipToPolicyHolder: undefined,
        policyHolderFirstName: undefined,
        policyHolderLastName: undefined,
        policyHolderDateOfBirth: undefined,
        insuranceCompanyName: undefined,
        insuranceId: undefined,
        insuranceCardFront: undefined,
        insuranceCardBack: undefined,
        memberId: undefined,
      };
    }

    if (data.fillAddressInfo && !isFillingAddressNow) {
      formData = {
        ...formData,
        homeAddress1: undefined,
        homeAddress2: undefined,
        city: undefined,
        state: undefined,
        zipCode: undefined,
      };
    }

    onSubmit(formData);
  };

  const contextValue: VisitFormContextValue<T> = {
    form,
    fieldErrorVisibility,
    insuranceCardFront,
    insuranceCardBack,
    setInsuranceCardFront: handleSetInsuranceCardFront,
    setInsuranceCardBack: handleSetInsuranceCardBack,
    isLoading,
    forceInsuranceNow,
  };

  return (
    <VisitFormContext.Provider value={contextValue}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
        className="space-y-8"
      >
        {children}
      </form>
    </VisitFormContext.Provider>
  );
}

type SubmitButtonProps = {
  children?: React.ReactNode;
  className?: string;
};

export function SubmitButton({
  children = "Confirm visit",
  className = "w-full max-w-md py-3 text-lg font-medium",
}: SubmitButtonProps) {
  const { isLoading } = useVisitFormContext();

  return (
    <div className="flex justify-center pt-6">
      <Button type="submit" isLoading={isLoading} className={className}>
        {isLoading ? "Submitting..." : children}
      </Button>
    </div>
  );
}

VisitForm.GeneralInfo = GeneralInfoSection;
VisitForm.ContactInfo = ContactInfoSection;
VisitForm.Address = AddressSection;
VisitForm.OptionalAddress = OptionalAddressSection;
VisitForm.ReferralSource = ReferralSourceSection;
VisitForm.Insurance = InsuranceSection;
VisitForm.OptionalInsurance = OptionalInsuranceSection;
VisitForm.Consents = ConsentsSection;
VisitForm.BookingNotes = BookingNotesSection;
VisitForm.CancellationPolicy = CancellationPolicySection;
export function SubmitSection() {
  return (
    <div className="!mt-5 flex flex-col gap-5">
      <StateDisclaimerSection />
      <SubmitButton />
    </div>
  );
}

VisitForm.StateDisclaimer = StateDisclaimerSection;
VisitForm.SubmitButton = SubmitButton;
VisitForm.SubmitSection = SubmitSection;
